// ============================================================
// 天水數位店長 — 週排班表
// ------------------------------------------------------------
// 【2026-09 改版原因】
// 舊版畫面上疊了三層左欄：App 側欄 224px ＋ 排班頁自己的左面板 208px
// ＋ 員工欄 160px = 592px。iPad 橫向 1024px 只剩約 430px 分給七天，
// 每格約 60px，「休息日」被壓成直排、字一個一個換行。
// 而那個左面板裡實際上只有兩個崗位勾選框，中間整片是空的。
//
// 改法：
//   1. 拿掉排班頁的左面板，發佈狀態／週切換／成本收進單列工具列
//      （崗位篩選移除——3 位在職員工、2 個崗位，篩選沒有意義）
//   2. 方格外層可橫向捲動、內層設最小寬度：窄螢幕改成左右滑，
//      不再壓縮格子。員工欄 sticky，滑動時姓名不會消失
//   3. 卡片固定高度、單行不換行；§36 說明移到 title 提示
//   4. 點格子 → ShiftPicker 選單（取代舊的「先選班別再點格子」）
// ============================================================

import { useEffect, useMemo, useState } from 'react';
import { useScheduleStore, type ShiftTemplate } from '../stores/scheduleStore';
import { ShiftPicker } from './ShiftPicker';

const DAY_NAMES = ['日', '一', '二', '三', '四', '五', '六'];

/**
 * 卡片上的時間顯示。格子寬度只有約 78px，完整的「08:00–17:00」放不下
 * 會被截斷；店裡的班別都是整點，因此整點時省略 :00 →「08–17」。
 * 非整點才顯示分鐘（如「08:30–17」）。滑鼠移上去仍可看到完整時間。
 */
function compactRange(start: string, end: string): string {
  const t = (v: string) => (v.endsWith(':00') ? v.slice(0, 2) : v);
  return `${t(start)}–${t(end)}`;
}

function getWeekDates(startDate: Date) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    return {
      label: `${d.getMonth() + 1}/${d.getDate()}`,
      dayFull: `週${DAY_NAMES[d.getDay()]}`,
      dateStr: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
      isWeekend: d.getDay() === 0 || d.getDay() === 6,
      isSunday: d.getDay() === 0,
    };
  });
}

/** 目前被點開選單的格子 */
interface PickerTarget {
  userId: string;
  staffName: string;
  dateStr: string;
  dayLabel: string;
  currentTemplateId: string | null;
  scheduleId: string | null;
  anchor: DOMRect;
}

export function WeeklyScheduler() {
  const {
    staff, templates, schedules, weekStart,
    costEstimate, weekInfo, isLoading,
    navigateWeek, setWeekStart, fetchStaffAndTemplates, fetchWeekSchedules,
    assignCell, clearCell, copyLastWeek, publishWeek, unpublishWeek,
  } = useScheduleStore();

  const [picker, setPicker] = useState<PickerTarget | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    fetchStaffAndTemplates();
    fetchWeekSchedules();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);

  // 排班快查：userId_dateStr → 該格的排班（同人同日只會有一筆，後端 upsert 保證）
  const scheduleMap = useMemo(() => {
    const map = new Map<string, typeof schedules[number]>();
    for (const s of schedules) map.set(`${s.userId}_${s.date.slice(0, 10)}`, s);
    return map;
  }, [schedules]);

  const dailyStaffCount = useMemo(() => {
    const counts = new Map<string, Set<string>>();
    for (const s of schedules) {
      const dk = s.date.slice(0, 10);
      if (!counts.has(dk)) counts.set(dk, new Set());
      counts.get(dk)!.add(s.userId);
    }
    return counts;
  }, [schedules]);

  // 每人本週累積時數（例假日／休息日不計；扣掉排班預設休息）
  const staffWeeklyHours = useMemo(() => {
    const hours = new Map<string, number>();
    for (const s of schedules) {
      const kind = s.template?.kind;
      if (kind === 'REGULAR_LEAVE' || kind === 'REST_DAY') continue;
      const [sh, sm] = s.startTime.split(':').map(Number);
      const [eh, em] = s.endTime.split(':').map(Number);
      let h = (eh * 60 + em - sh * 60 - sm) / 60;
      if (h < 0) h += 24;
      if (s.breakStart && s.breakEnd) {
        const [bsH, bsM] = s.breakStart.split(':').map(Number);
        const [beH, beM] = s.breakEnd.split(':').map(Number);
        h -= ((beH * 60 + beM) - (bsH * 60 + bsM)) / 60;
      }
      hours.set(s.userId, (hours.get(s.userId) ?? 0) + Math.max(0, h));
    }
    return hours;
  }, [schedules]);

  const dailyCostMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of costEstimate?.dailyBreakdown ?? []) {
      const ds = typeof d.date === 'string' ? d.date.slice(0, 10) : new Date(d.date).toISOString().slice(0, 10);
      map.set(ds, d.estimatedCost);
    }
    return map;
  }, [costEstimate]);

  const holidays = weekInfo?.holidays ?? {};
  const dailyRevenue = weekInfo?.dailyRevenue ?? {};
  const publishStatus = weekInfo?.publishStatus ?? 'EMPTY';
  const totalCost = costEstimate?.totalLaborCost ?? 0;
  const alerts = costEstimate?.alerts ?? [];
  const isEmptyWeek = schedules.length === 0;

  const dailyCostPct = (dateStr: string): string | null => {
    const cost = dailyCostMap.get(dateStr);
    const rev = dailyRevenue[dateStr];
    if (cost && rev && rev > 0) return ((cost / rev) * 100).toFixed(1) + '%';
    return null;
  };

  // ── 動作 ──
  const openPicker = (e: React.MouseEvent, staffId: string, staffName: string, d: typeof weekDates[number]) => {
    const entry = scheduleMap.get(`${staffId}_${d.dateStr}`);
    setPicker({
      userId: staffId,
      staffName,
      dateStr: d.dateStr,
      dayLabel: `${d.dayFull} ${d.label}`,
      currentTemplateId: entry?.templateId ?? null,
      scheduleId: entry?.id ?? null,
      anchor: (e.currentTarget as HTMLElement).getBoundingClientRect(),
    });
  };

  const handlePick = async (t: ShiftTemplate) => {
    if (!picker) return;
    const { userId, dateStr } = picker;
    setPicker(null);
    await assignCell(userId, dateStr, t);
  };

  const handleClear = async () => {
    if (!picker?.scheduleId) return;
    const id = picker.scheduleId;
    setPicker(null);
    await clearCell(id);
  };

  const handleCopyLastWeek = async () => {
    if (!window.confirm('要把上一週的班表複製到本週嗎？\n\n複製後為未發布狀態，你可以先修改再發布。')) return;
    setBusy(true);
    try {
      const n = await copyLastWeek();
      setToast(n > 0 ? `已複製 ${n} 筆排班，請確認後再發布` : '上一週沒有排班可以複製');
    } catch (err) {
      setToast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handlePublish = async () => {
    setBusy(true);
    try { setToast(`已發布 ${await publishWeek()} 筆排班`); }
    finally { setBusy(false); }
  };

  const handleUnpublish = async () => {
    if (!window.confirm('確定要取消發布本週班表？\n\n取消後員工將看不到此班表，需重新發布才會生效。')) return;
    setBusy(true);
    try { setToast(`已取消發布 ${await unpublishWeek()} 筆排班`); }
    finally { setBusy(false); }
  };

  const goThisWeek = () => {
    const d = new Date();
    d.setDate(d.getDate() + (d.getDay() === 0 ? -6 : 1 - d.getDay()));
    setWeekStart(d);
  };

  return (
    <div className="min-h-page flex flex-col bg-morandi-50">
      {/* ─── 工具列 ─────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 bg-white border-b border-morandi-200">
        {/* 週切換 */}
        <div className="flex items-center gap-1">
          <button onClick={() => navigateWeek(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-btn hover:bg-morandi-100 text-morandi-500 text-lg">‹</button>
          <span className="text-sm font-mono font-medium text-morandi-700 min-w-[104px] text-center">
            {weekDates[0].label} – {weekDates[6].label}
          </span>
          <button onClick={() => navigateWeek(1)}
            className="w-9 h-9 flex items-center justify-center rounded-btn hover:bg-morandi-100 text-morandi-500 text-lg">›</button>
          <button onClick={goThisWeek}
            className="ml-1 px-2.5 py-1.5 text-xs rounded-btn text-morandi-500 hover:bg-morandi-100">本週</button>
        </div>

        {/* 成本 */}
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-morandi-400">人事成本</span>
          <span className="text-base font-mono font-bold text-morandi-800">${totalCost.toLocaleString()}</span>
          {alerts.length > 0 && (
            <span className="text-xs text-rose-500">{alerts.length} 項警示</span>
          )}
        </div>

        {isLoading && <span className="text-xs text-morandi-400 animate-pulse-soft">載入中…</span>}

        {/* 右側動作 */}
        <div className="flex items-center gap-2 ml-auto">
          {/* 只在本週完全沒排班時提供，避免覆蓋已排好的班表 */}
          {isEmptyWeek && (
            <button onClick={handleCopyLastWeek} disabled={busy}
              className="px-3 py-2 text-sm rounded-btn border border-morandi-200 text-morandi-600 hover:bg-morandi-50 disabled:opacity-50">
              複製上週
            </button>
          )}
          <span className={`px-2.5 py-1 rounded-chip text-xs font-medium
            ${publishStatus === 'PUBLISHED' ? 'bg-sage-100 text-sage-700'
              : publishStatus === 'DRAFT' ? 'bg-earth-100 text-earth-700'
              : 'bg-morandi-100 text-morandi-400'}`}>
            {publishStatus === 'PUBLISHED' ? '已發布' : publishStatus === 'DRAFT' ? '未發布' : '尚無排班'}
          </span>
          {publishStatus === 'PUBLISHED' ? (
            <button onClick={handleUnpublish} disabled={busy}
              className="px-3 py-2 text-sm rounded-btn border border-rose-300 text-rose-500 hover:bg-rose-50 disabled:opacity-50">
              取消發布
            </button>
          ) : (
            <button onClick={handlePublish} disabled={busy || isEmptyWeek}
              className="px-4 py-2 text-sm rounded-btn bg-sage-500 text-white font-medium hover:bg-sage-600 disabled:opacity-40">
              發布班表
            </button>
          )}
        </div>
      </div>

      {toast && (
        <div className="mx-4 mt-3 px-3 py-2 rounded-cell bg-sage-50 border border-sage-200 text-sm text-sage-700 animate-slide-up">
          {toast}
        </div>
      )}

      {/* ─── 方格 ───────────────────────────────── */}
      <div className="flex-1 p-4 min-h-0">
        <div className="h-full bg-white rounded-card shadow-card overflow-hidden flex flex-col">
          {/* 橫向捲動容器：窄螢幕用滑的，不壓縮格子 */}
          <div className="flex-1 overflow-auto">
            <div className="min-w-[726px]">
              {/* 表頭 */}
              <div className="grid grid-cols-[96px_repeat(7,minmax(90px,1fr))] border-b border-morandi-200 sticky top-0 z-20 bg-white">
                <div className="p-2 bg-morandi-100 border-r border-morandi-200 flex items-center sticky left-0 z-10">
                  <span className="text-xs text-morandi-400 font-medium">員工</span>
                </div>
                {weekDates.map(d => {
                  const count = dailyStaffCount.get(d.dateStr)?.size ?? 0;
                  const holiday = holidays[d.dateStr];
                  const pct = dailyCostPct(d.dateStr);
                  return (
                    <div key={d.dateStr}
                      className={`p-2 text-center border-r border-morandi-200 last:border-r-0
                        ${holiday ? 'bg-rose-50' : d.isSunday ? 'bg-morandi-100' : d.isWeekend ? 'bg-morandi-50' : ''}`}>
                      {holiday && <div className="text-xs text-rose-500 font-medium truncate mb-0.5" title={holiday}>{holiday}</div>}
                      <div className={`text-xs ${d.isWeekend || holiday ? 'text-rose-500' : 'text-morandi-400'}`}>{d.dayFull}</div>
                      <div className="text-sm font-mono font-medium text-morandi-700">{d.label}</div>
                      {pct && (
                        <div className={`text-xs mt-0.5 font-mono ${parseFloat(pct) > 30 ? 'text-rose-500' : 'text-sage-600'}`}>{pct}</div>
                      )}
                      <div className={`text-xs mt-0.5 ${count < 2 ? 'text-rose-500 font-medium' : 'text-morandi-300'}`}>{count} 人</div>
                    </div>
                  );
                })}
              </div>

              {/* 員工列 */}
              {staff.map(s => {
                const weekHours = staffWeeklyHours.get(s.id) ?? 0;
                const over = weekHours > 40;
                return (
                  <div key={s.id} className="grid grid-cols-[96px_repeat(7,minmax(90px,1fr))] border-b border-morandi-100 last:border-b-0">
                    {/* 員工欄：滑動時固定在左邊 */}
                    {/* 不放頭像：欄寬只有 96px，32px 的單字頭像會把姓名擠成「劉…」。
                        密集表格裡姓名本身就是識別，頭像沒有額外價值。 */}
                    <div className="px-2 py-2 border-r border-morandi-200 flex flex-col justify-center sticky left-0 z-10 bg-white">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-morandi-700 truncate" title={s.name}>{s.name}</div>
                        <div className={`text-xs font-mono ${over ? 'text-rose-500 font-medium' : 'text-morandi-400'}`}
                          title={over ? '本週工時超過 40 小時' : undefined}>
                          {Math.round(weekHours)} hr{over && ' ⚠'}
                        </div>
                      </div>
                    </div>

                    {/* 每日格子 */}
                    {weekDates.map(d => {
                      const entry = scheduleMap.get(`${s.id}_${d.dateStr}`);
                      const kind = entry?.template?.kind ?? 'WORK';
                      const holiday = holidays[d.dateStr];
                      const isOpen = picker?.userId === s.id && picker?.dateStr === d.dateStr;

                      return (
                        <div key={d.dateStr}
                          onClick={(e) => openPicker(e, s.id, s.name, d)}
                          className={`h-[76px] p-1.5 border-r border-morandi-100 last:border-r-0 cursor-pointer transition-colors
                            ${isOpen ? 'ring-2 ring-inset ring-sage-400' : ''}
                            ${!entry ? 'hover:bg-morandi-50' : ''}
                            ${!entry && holiday ? 'bg-rose-50/40' : ''}
                            ${!entry && d.isWeekend && !holiday ? 'bg-morandi-50/30' : ''}`}
                        >
                          {entry ? (
                            kind === 'REGULAR_LEAVE' || kind === 'REST_DAY' ? (
                              <div
                                className="h-full rounded-cell flex items-center justify-center px-1.5"
                                style={{
                                  backgroundColor: (entry.template?.color ?? '#7E8FA3') + '1F',
                                  borderLeft: `3px solid ${entry.template?.color ?? '#7E8FA3'}`,
                                }}
                                title={kind === 'REGULAR_LEAVE' ? '例假日（勞基法 §36，不可加班）' : '休息日（勞基法 §36，可加班）'}
                              >
                                <span className="text-xs font-medium whitespace-nowrap truncate"
                                  style={{ color: entry.template?.color ?? '#7E8FA3' }}>
                                  {kind === 'REGULAR_LEAVE' ? '📌 例假日' : '💤 休息日'}
                                </span>
                              </div>
                            ) : (
                              <div
                                className="h-full rounded-cell px-1.5 py-1.5 flex flex-col justify-center gap-0.5"
                                style={{
                                  backgroundColor: (entry.template?.color ?? '#8B9DAF') + '1F',
                                  borderLeft: `3px solid ${entry.template?.color ?? '#8B9DAF'}`,
                                }}
                              >
                                <div className="text-xs font-medium whitespace-nowrap truncate"
                                  style={{ color: entry.template?.color ?? '#8B9DAF' }}>
                                  {entry.template?.name ?? '自訂'}
                                </div>
                                <div className="text-[11px] font-mono text-morandi-400 whitespace-nowrap"
                                  title={`${entry.startTime} – ${entry.endTime}`}>
                                  {compactRange(entry.startTime, entry.endTime)}
                                </div>
                                {entry.isPublished && <div className="text-[11px] text-sage-500 leading-none">✓ 已發布</div>}
                              </div>
                            )
                          ) : (
                            <div className="h-full flex items-center justify-center opacity-0 hover:opacity-40 transition-opacity">
                              <span className="text-xl text-morandi-400">＋</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              {staff.length === 0 && (
                <div className="flex items-center justify-center py-20 text-morandi-300 text-sm">載入員工資料中…</div>
              )}
            </div>
          </div>
        </div>

        {/* 勞基法警示 */}
        {alerts.length > 0 && (
          <div className="mt-3 space-y-1">
            {alerts.slice(0, 5).map((alert, i) => {
              // 警示訊息裡是員工 id，換成看得懂的名字
              let msg = alert.message;
              for (const s of staff) if (msg.includes(s.id)) msg = msg.replaceAll(s.id, s.name);
              return (
                <div key={i} className={`px-3 py-2 rounded-cell text-sm
                  ${alert.severity === 'ERROR'
                    ? 'bg-rose-50 text-rose-600 border border-rose-200'
                    : 'bg-smoke-50 text-smoke-700 border border-smoke-200'}`}>
                  {msg}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {picker && (
        <ShiftPicker
          staffName={picker.staffName}
          dayLabel={picker.dayLabel}
          templates={templates}
          currentTemplateId={picker.currentTemplateId}
          hasSchedule={!!picker.scheduleId}
          anchor={picker.anchor}
          onPick={handlePick}
          onClear={handleClear}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}
